import { useDispatch, useSelector } from 'react-redux';
import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { selectV2, setStep, setFocusField } from '../v2Upload.slice';
import { STEP_LABELS } from '../stepLabels';

const FILES_STEP = STEP_LABELS.indexOf('Add files');

function getStepButtonClass(isDone: boolean, isNow: boolean): string {
  // Every state carries a border, or the row jumps 1px when one appears.
  if (isNow) {
    return 'bg-accent border border-primary/30 text-foreground font-medium';
  }
  if (isDone) {
    return 'border border-transparent text-foreground hover:border-border-strong hover:bg-muted';
  }
  return 'border border-transparent text-muted-foreground hover:border-border-strong hover:bg-muted/50';
}

function getStepBadgeClass(isDone: boolean, isNow: boolean): string {
  // `isNow` first. A step can now be both — you can stand on one you already
  // completed by walking back to it — and "where you are" is the more useful
  // thing to show.
  if (isNow) {
    return 'bg-primary text-white';
  }
  if (isDone) {
    return 'bg-green-500 text-white';
  }
  return 'border border-muted-foreground/40';
}

function getStepStatusText(isDone: boolean, isNow: boolean): string {
  if (isNow) {
    return 'now';
  }
  if (isDone) {
    return 'done';
  }
  return '';
}

/** A step is closed only when frozen or never reached. */
export function isStepLocked(
  i: number,
  state: { frozen: boolean; maxStep: number }
): boolean {
  return state.frozen || i > state.maxStep;
}

/** True when the click should send the user to the unticked carve-order box
 * rather than to the step they asked for. */
export function divertsToOrderGate(
  i: number,
  state: { orderPending: boolean }
): boolean {
  return state.orderPending && i > FILES_STEP;
}

export default function StepNavigation({
  frozen = false,
}: {
  frozen?: boolean;
}) {
  const dispatch = useDispatch();
  const { step, maxStep, files, orderConfirmed } = useSelector(selectV2);
  const orderPending = files.some((f) => f.kind === 'gcode') && !orderConfirmed;

  // Show the gap rather than a dead button: land on Add files and ring the box.
  const go = (i: number) => {
    if (divertsToOrderGate(i, { orderPending })) {
      dispatch(setStep(FILES_STEP));
      dispatch(setFocusField('order-confirmed'));
      return;
    }
    dispatch(setStep(i));
  };

  return (
    // Named landmark: the footer's "Continue to Add files" would otherwise be
    // indistinguishable from the rail's own "Add files".
    <nav aria-label="Wizard steps" className="flex flex-col gap-1.5">
      {
        // fallow-ignore-next-line complexity
        STEP_LABELS.map((label, i) => {
          // Against maxStep, not step: a step stays done once it has been done,
          // even after walking back past it.
          const isDone = frozen || i < maxStep;
          const isNow = !frozen && i === step;
          const statusText = getStepStatusText(isDone, isNow);
          const isLocked = isStepLocked(i, { frozen, maxStep });

          return (
            <button
              key={label}
              type="button"
              disabled={isLocked}
              onClick={() => go(i)}
              className={cn(
                'flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-left text-sm transition-[color,background-color,border-color] duration-fast',
                getStepButtonClass(isDone, isNow),
                isLocked && !frozen && 'cursor-not-allowed opacity-60'
              )}
            >
              <span
                className={cn(
                  'w-[18px] h-[18px] rounded-full flex items-center justify-center text-[9px] flex-none transition-[color,background-color,border-color] duration-fast',
                  getStepBadgeClass(isDone, isNow)
                )}
              >
                {/* Same precedence as the badge colour: standing on a completed
                  step shows its number, not a tick, so "you are here" reads. */}
                {/* Numbered from 0 so Choose Type is step 0 and the six real
                  steps keep the numbers users already know. */}
                {isDone && !isNow ? <Check className="w-2.5 h-2.5" /> : i}
              </span>
              <span className="flex-1">{label}</span>
              <span className="text-[10px] text-muted-foreground">
                {statusText}
              </span>
            </button>
          );
        })
      }
    </nav>
  );
}
